import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { getEntityConfiguration } from './configurations';
import { EntityType, MergeEntityData } from './types';
import { GenericDataSection } from './GenericDataSection';
import { LineupsSection } from './LineupsSection';

interface GenericEntityDataAccordionsProps {
  data: MergeEntityData;
  entityId: number;
  entityType: EntityType;
  onLoadMore: (entityId: number, dataType: string) => void;
}

export const GenericEntityDataAccordions = ({
  data,
  entityId,
  entityType,
  onLoadMore,
}: GenericEntityDataAccordionsProps) => {
  if (data?.loading || !data) {
    return <Typography>Loading...</Typography>;
  }

  const configuration = getEntityConfiguration(entityType);
  const dataTypes = Object.keys(configuration);

  if (dataTypes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No additional data available for this entity type.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {dataTypes.map(dataType => {
        const sectionData = data[dataType];
        if (!sectionData || typeof sectionData === 'boolean') {
          return null;
        }

        return (
          <Accordion key={dataType}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                {getDataTypeDisplayName(dataType)} ({sectionData.totalItems})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {dataType === 'lineups' ? (
                <LineupsSection data={sectionData} />
              ) : (
                <GenericDataSection
                  dataType={dataType}
                  data={sectionData}
                  onLoadMore={() => onLoadMore(entityId, dataType)}
                />
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

const getDataTypeDisplayName = (dataType: string): string => {
  const displayNameMap: Record<string, string> = {
    contracts: 'Contracts',
    fixtures: 'Fixtures',
    seasons: 'Seasons',
    rounds: 'Rounds',
    competitions: 'Competitions',
    seasoncompetitors: 'Season Competitors',
    officials: 'Officials',
    lineups: 'Used in lineups',
    sports: 'Related sports',
    homeCompetitors: 'Home venue of competitors',
    subVenues: 'Sub-venues',
  };

  return displayNameMap[dataType] || dataType.charAt(0).toUpperCase() + dataType.slice(1);
};
